import type { InterpolatedRequest } from '../data-sources/fetchDataSource';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';

/**
 * A post-fetch response transform run on the raw JSON before path extraction.
 * Receives the source declaration so per-source flags (e.g. `bucketRelated`)
 * can gate a coercer's behavior; the arg is optional for standalone callers.
 */
export type ResponseCoercer = (
  res: unknown,
  req: InterpolatedRequest,
  decl?: DataSourceDeclaration,
) => unknown;

/**
 * Chain multiple response coercers into one, applied left-to-right — each
 * receives the previous coercer's output. Mirrors
 * {@link composeRequestBodyCoercers} for the response side so
 * `ConfigurableStep` can pass a single `coerceResponse` to `fetchDataSource`
 * regardless of how many service-shaped normalizers are active.
 *
 * `undefined` entries (a coercer that isn't applicable to the current
 * `apiConfig`) are dropped. Returns `undefined` when none are active so the
 * caller can pass the result straight through without a null branch; returns
 * the single coercer unwrapped when exactly one is active. Because each
 * coercer is individually gated on request method/path/origin, ordering only
 * matters when two coercers claim the same route — which they do not here.
 */
export function composeResponseCoercers(
  ...coercers: ReadonlyArray<ResponseCoercer | undefined>
): ResponseCoercer | undefined {
  const active = coercers.filter((c): c is ResponseCoercer => c !== undefined);
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  return (res, req, decl) =>
    active.reduce((acc, coerce) => coerce(acc, req, decl), res);
}
